import { DescontoGrupos } from "./desconto-grupos";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, FilterChips, Pagination, Pill, type Column , fmtBRL, fmtDataISO} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { formatBackendErr } from "../../lib/format-backend-err";
import { AndamentoEnvio } from "../../components/AndamentoEnvio";
import type { AdminEstadoDaFolha, AdminFolha } from "@atlas/sdk";
import { EmitirMovimentoModal } from "./emitir-movimento-modal";
// `contagens`/`recortar` sairam daqui na 007: os dois operavam sobre a lista
// inteira, que a tela nao tem mais. O equivalente roda na rota.
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";

export function AdminFolhas() {
  const [folhaDistribuicao, setFolhaDistribuicao] = useState<string | null>(null);
  const qc = useQueryClient();
  // Poll 5s enquanto a tela esta aberta — quando o operador clica "Aplicar em
  // folha" em /averbadora/adf, os contadores aqui atualizam em ate 5s.

  /**
   * EMITIR o movimento — o ato do corte.
   *
   * O diálogo cuida do ato inteiro: o motivo da retificação (a rota recusa sem
   * ele), o andamento enquanto gera e o resultado. Antes eram `window.prompt`
   * e `alert()`, e no meio só um botão desabilitado — em folha grande, minutos
   * sem nenhum sinal de vida (ALECE 202609, 416 s).
   */
  const [folhaEmitindo, setFolhaEmitindo] = useState<AdminFolha | null>(null);

  /** Sobrescrever o corte desta competência — a exceção que a regra não prevê. */
  const corte = useMutation({
    mutationFn: (f: AdminFolha) => {
      const atual = f.estado?.corteEm ?? "";
      const quando = window.prompt(
        `Corte de ${f.competencia}\n\nAtual: ${fmtInstante(atual)}\n\n` +
        "Informe o novo instante (AAAA-MM-DDTHH:MM). Use isto para determinação dos gestores " +
        "ou feriado municipal — o cálculo automático só conhece fim de semana.",
        atual.slice(0, 16),
      );
      if (quando === null) throw new Error("cancelado");
      const motivo = window.prompt("Por que o corte desta competência é diferente?", "");
      if (motivo === null) throw new Error("cancelado");
      if (motivo.trim().length < 5) throw new Error("Escreva o motivo — depois alguém vai perguntar por que aquele mês foi diferente.");
      // Offset explícito: o corte é horário local, e sem ele a comparação com
      // "agora" ficaria ambígua por três horas.
      const em = /[+-]\d{2}:\d{2}$/.test(quando) ? quando : `${quando}:00-03:00`.replace(/:00:00-03:00$/, ":00-03:00");
      return atlas.admin.sobrescreverCorte(f.id, em, motivo.trim());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "folhas"] }),
    onError: (e) => { if (e instanceof Error && e.message !== "cancelado") alert(e.message); },
  });

  /**
   * Consolidar — o momento em que o valor vira final.
   *
   * A confirmacao do incompleto e' pedida AQUI, antes da chamada, e nao descoberta
   * por um 400. A API tambem recusa sem ela (a regra nao mora na tela), mas
   * mostrar o custo antes do clique e' a diferenca entre a averbadora decidir e a
   * averbadora ser corrigida.
   */
  const consolidar = useMutation({
    mutationFn: (f: AdminFolha) => {
      const falta = f.estado?.partesFaltando ?? [];
      const pendentes = f.estado?.recebidosNaoProcessados ?? 0;
      // As duas confirmacoes vao no MESMO parametro: a API trata parte faltando e
      // arquivo parado do mesmo jeito (os dois sao presumidos como descontados).
      let confirmou = false;
      if (pendentes > 0) {
        const segue = window.confirm(
          `${pendentes} arquivo(s) de retorno desta competência foram recebidos e AINDA NÃO processados.\n\n`
          + "Consolidar agora presume como DESCONTADO o que está neles, e a competência fecha como PRESUMIDA. "
          + "Processe-os primeiro em Troca de arquivos.\n\n"
          + "Consolidar mesmo assim?",
        );
        if (!segue) throw new Error("cancelado");
        confirmou = true;
      }
      if (falta.length > 0) {
        const segue = window.confirm(
          `Falta a parte ${falta.join(", ")} do retorno de ${f.competencia}.\n\n`
          + "Consolidar presume essa(s) parte(s) como DESCONTADA(S): libera margem de dívida viva "
          + "e atribui repasse a quem pode não ter recebido. A competência fecha como PRESUMIDA.\n\n"
          + "Confirmar?",
        );
        if (!segue) throw new Error("cancelado");
        confirmou = true;
      }
      return confirmou
        ? atlas.admin.consolidarFolha(f.id, { confirmarIncompleto: true })
        : atlas.admin.consolidarFolha(f.id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "folhas"] }),
    // `formatBackendErr`: a recusa vem como 400 `validation_error`, cuja mensagem
    // de topo e' o generico "Dados invalidos". O motivo real esta em `details`.
    onError: (e) => { if (!(e instanceof Error && e.message === "cancelado")) alert(formatBackendErr(e)); },
  });

  /**
   * PROCESSAR os retornos recebidos desta competencia.
   *
   * O upload da prefeitura guarda e valida; este ato aplica — grava resultados,
   * avanca parcela, interrompe contrato de desligado, libera margem. Antes tudo
   * isso acontecia no request de quem enviava, e nao havia momento em que o
   * arquivo estivesse "recebido, ainda nao aplicado".
   *
   * Processa TODOS os recebidos da competencia em sequencia, e nao em paralelo: os
   * dois arquivos de uma folha de duas partes mexem nos mesmos contratos, e a
   * ingestao nao foi feita para rodar concorrente sobre a mesma competencia.
   */
  const processar = useMutation({
    mutationFn: async (f: AdminFolha) => {
      const lista = await atlas.admin.trocaArquivos({
        prefeitura_id: f.unidadeId, competencia: f.competencia, tipo: "retorno", limit: 50,
      });
      const recebidos = (lista.arquivos ?? []).filter((a) => a.status === "recebido" && !a.excluidoEm);
      if (recebidos.length === 0) throw new Error("Nenhum retorno recebido aguardando processamento nesta competência.");

      const semPacote = recebidos.filter((a) => a.temPacote === false);
      if (semPacote.length > 0) {
        throw new Error(
          `${semPacote.length} envio(s) desta competência não guardaram o arquivo e não podem ser processados `
          + "(são anteriores ao modo manual). Peça o reenvio ao órgão.",
        );
      }

      const resultados = [];
      for (const a of recebidos) resultados.push(await atlas.admin.processarRetorno(a.id));
      return resultados;
    },
    onSuccess: (rs) => {
      qc.invalidateQueries({ queryKey: ["admin", "folhas"] });
      const somar = (k: "resultados" | "desligamentos" | "nao_solicitados" | "linhas_rejeitadas") =>
        rs.reduce((t, r) => t + (r[k] ?? 0), 0);
      alert(
        `${rs.length} arquivo(s) processado(s).\n\n`
        + `${somar("resultados")} resultado(s) de desconto aplicado(s)\n`
        + `${somar("desligamentos")} desligamento(s) declarado(s)\n`
        + `${somar("nao_solicitados")} desconto(s) desconhecido(s) em conciliação\n`
        + `${somar("linhas_rejeitadas")} linha(s) rejeitada(s)`,
      );
    },
    onError: (e) => { if (e instanceof Error && e.message !== "cancelado") alert(e.message); },
  });
  // Averbadora NAO fecha nem exclui folha — regra reafirmada pelo cliente
  // 24/07/2026: fechar e' acao da PREFEITURA, excluir tambem. Averbadora so
  // consolida (fechada -> consolidada).

  const columns: Column<AdminFolha>[] = [
    { key: "id", header: "ID", mono: true },
    { key: "prefeitura", header: "Órgão/entidade" },
    { key: "competencia", header: "Competência" },
    // "Data corte" saiu daqui em 14/09/2026: era `f.dataCorte`, congelado na
    // criação da folha, ao lado da coluna "Corte" (o instante da regra) — e as
    // duas discordavam (08/09 × 15/09 17:00 na mesma linha). O corte é UM, e
    // está na coluna "Corte". `dataCorte` continua na API, pela mesma regra.
    { key: "dataRepasse", header: "Repasse", render: (f) => fmtDataISO(f.dataRepasse) },
    {
      key: "adfsAplicadas",
      header: "ADFs aplicadas",
      align: "right",
      render: (f) => {
        const aplic = f.adfsAplicadas ?? 0;
        const total = f.adfsTotal ?? 0;
        if (total === 0) return <span style={{ color: "var(--text-dim)" }}>—</span>;
        return (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            <b style={{ color: aplic > 0 ? "var(--emerald-500)" : "var(--text)" }}>{aplic}</b>
            <span style={{ color: "var(--text-dim)" }}> / {total}</span>
          </span>
        );
      },
    },
    {
      key: "valorAplicado",
      header: "Valor aplicado",
      align: "right",
      render: (f) => {
        const v = f.valorAplicado ?? 0;
        if (v === 0) return <span style={{ color: "var(--text-dim)" }}>—</span>;
        return <span style={{ color: "var(--emerald-500)", fontWeight: 600 }}>{fmtBRL(v)}</span>;
      },
    },
    {
      key: "corte",
      header: "Corte",
      width: 180,
      render: (f) => {
        const e = f.estado;
        if (!e) return <span style={{ opacity: 0.5 }}>—</span>;
        return (
          <div style={{ display: "grid", gap: 2 }}>
            {/* INSTANTE, não data: o comunicado real diz "dia 15, a partir das
                17:00", e às 16:59 ainda entra averbação. */}
            <span style={{ fontSize: 12 }}>{fmtInstante(e.corteEm)}</span>
            <span style={{ fontSize: 11, color: e.cortePassou ? "var(--text-dim)" : "var(--gold-500)" }}>
              {e.cortePassou ? "passou" : "ainda entram averbações"}
              {e.corteSobrescrito ? " · sobrescrito" : ""}
            </span>
          </div>
        );
      },
    },
    {
      key: "movimento",
      header: "Movimento",
      width: 190,
      render: (f) => {
        if (!f.movimentoEmitido) {
          return <span style={{ fontSize: 12, color: "var(--gold-500)" }}>não emitido</span>;
        }
        const m = f.movimentoEmitido;
        return (
          <div style={{ display: "grid", gap: 2 }}>
            <span style={{ fontSize: 12 }} title={`hash ${m.hash}`}>
              {m.retificacaoDe ? "retificado" : "emitido"} · {fmtInstante(m.em)}
            </span>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>por {m.porNome}</span>
          </div>
        );
      },
    },
    {
      key: "retorno",
      header: "Retorno",
      width: 170,
      render: (f) => <CelulaRetorno estado={f.estado} />,
    },
    {
      key: "status",
      header: "Status",
      width: 150,
      render: (f) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Pill variant={f.status === "consolidada" ? "averbado" : f.status === "fechada" ? "emdia" : "pendente"}>{f.status}</Pill>
          {f.conferencia ? (
            <span
              style={{ fontSize: 11, color: f.conferencia === "presumida" ? "var(--gold-500)" : "var(--text-dim)" }}
              title={f.conferencia === "presumida"
                ? "Fechou pelo prazo de repasse, sem o retorno completo: os descontos entraram como aplicados e ninguém confirmou."
                : "O retorno do órgão confirmou os descontos."}
            >
              {f.conferencia}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "acoes",
      header: "",
      align: "right",
      render: (f) => (
        <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {/* EMITIR é o ato do corte, e é da averbadora. Antes não existia: o
              movimento era gerado quando a prefeitura baixava. */}
          {f.status !== "consolidada" ? (
            <Button
              size="sm"
              type="button"
              variant={f.movimentoEmitido ? "ghost" : "primary"}
              disabled={folhaEmitindo !== null || !!f.estado?.retornoCompleto}
              onClick={() => setFolhaEmitindo(f)}
              title={f.estado?.retornoCompleto
                ? "A folha já foi processada (retorno aplicado). Não é possível retificar/emitir o movimento desta competência."
                : f.movimentoEmitido
                  ? "Reemitir é uma RETIFICAÇÃO: exige motivo, e as duas versões ficam no histórico."
                  : "Gera e CONGELA o movimento desta competência. A partir daqui o órgão baixa este arquivo."}
            >
              {f.movimentoEmitido ? "↻ Retificar" : "⇪ Emitir movimento"}
            </Button>
          ) : null}

          {f.status !== "consolidada" ? (
            <Button
              size="sm"
              type="button"
              variant="ghost"
              disabled={!!f.movimentoEmitido}
              onClick={() => corte.mutate(f)}
              title={f.movimentoEmitido
                ? "O movimento já foi emitido — mover o corte não altera o arquivo entregue."
                : "Determinação dos gestores, ou feriado municipal (o cálculo de dia útil só conhece fim de semana)."}
            >
              ⏱ Corte
            </Button>
          ) : null}

          {/* PROCESSAR aparece so' quando ha' o que processar. Um botao permanente
              desabilitado ocuparia a linha de acoes em toda folha da lista e diria
              menos do que a ausencia dele. */}
          {(f.estado?.recebidosNaoProcessados ?? 0) > 0 ? (
            <Button
              size="sm"
              type="button"
              disabled={processar.isPending}
              onClick={() => processar.mutate(f)}
              title={`${f.estado?.recebidosNaoProcessados} arquivo(s) recebido(s) e não aplicado(s). `
                + "Processar grava os resultados, avança as parcelas e libera margem."}
            >
              ▶ Processar {f.estado?.recebidosNaoProcessados}
            </Button>
          ) : null}

          {(f.estado?.retornosProcessados ?? 0) > 0 || f.estado?.retornoCompleto ? (
            <Button
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => setFolhaDistribuicao(f.id)}
              title="Consultar o valor descontado pela ALECE e distribuí-lo entre os contratos candidatos."
            >
              Distribuição
            </Button>
          ) : null}

          {f.status !== "consolidada" ? (
            <Button
              size="sm"
              type="button"
              disabled={consolidar.isPending || !f.estado?.podeConsolidar}
              onClick={() => consolidar.mutate(f)}
              title={!f.estado?.podeConsolidar
                ? ((f.estado?.recebidosNaoProcessados ?? 0) > 0
                    ? "Há retorno recebido e não processado. Processe antes de consolidar."
                    : "Nenhum retorno processado, e o prazo de repasse não venceu. Não há o que consolidar.")
                : f.estado.liberadoPeloPrazo
                  ? "Liberado pelo PRAZO de repasse, sem nenhum retorno processado. Vai fechar como PRESUMIDA."
                  : f.estado.partesFaltando.length > 0
                    ? `Falta ${f.estado.partesFaltando.join(", ")} — vai pedir confirmação e fechar como PRESUMIDA.`
                    : "Retorno completo. Cada contrato da Atlas avança +1 parcela paga."}
            >
              ✓ Consolidar
            </Button>
          ) : (
            <span style={{ fontSize: 11, color: "var(--text-dim)", padding: "6px 8px" }}>—</span>
          )}
        </div>
      ),
    },
  ];

  // O botao "Processar" fica dentro da linha; o andamento fica aqui, fora da
  // tabela, porque processar um retorno recebido e' a mesma ingestao da carga
  // inicial e responde so' no fim. O rotulo diz QUAL folha esta rodando.
  const emProcessamento = processar.variables;
  const nomeOrgao = emProcessamento?.prefeitura;
  const rotuloProcessando = emProcessamento
    ? `Processando o retorno de ${nomeOrgao} · ${emProcessamento.competencia}`
    : "Processando";

  if (folhaDistribuicao) {
    return <DescontoGrupos folhaId={folhaDistribuicao} onClose={() => setFolhaDistribuicao(null)} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Folhas por competência</h1>
        <p style={{ color: "var(--text-muted)" }}>Estado das folhas das prefeituras afiliadas (aberta → fechada → consolidada). Só a averbadora consolida.</p>
      </header>

      <AndamentoEnvio
        ativo={processar.isPending}
        verbo={rotuloProcessando}
        expectativa="Uma folha com alguns milhares de servidores leva em torno de um minuto por arquivo."
        consequencia="Não saia desta página: cada arquivo entra inteiro ou não entra, e o resumo aparece no fim."
      />

      <FolhasFiltros columns={columns} />

      {folhaEmitindo ? (
        <EmitirMovimentoModal
          folha={folhaEmitindo}
          onClose={() => setFolhaEmitindo(null)}
          fmtInstante={fmtInstante}
        />
      ) : null}

    </div>
  );
}

/** Filtros aplicados client-side (todas as folhas ja vem no /admin/folhas):
 *   - Prefeitura (dropdown com as prefeituras que tem folha)
 *   - Competencia INI/FIM (YYYY-MM inputs)
 *  Default: ultimas 12 competencias, todas prefeituras. Ordena DESC (mais
 *  recente primeiro) — cliente pediu pra evitar poluicao visual quando a
 *  base envelhecer (5 anos = 60 folhas por prefeitura). */
function FolhasFiltros({ columns }: { columns: Column<AdminFolha>[] }) {
  // YYYY-MM (input browser). Backend usa YYYYMM sem separador.
  const compToInput = (c: string): string => /^\d{6}$/.test(c) ? `${c.slice(0, 4)}-${c.slice(4, 6)}` : c;
  const inputToComp = (v: string): string => v.replace(/[^0-9]/g, "");

  const [prefFiltro, setPrefFiltro] = useState<number | "">("");
  const [compIni, setCompIni] = useState("");
  const [compFim, setCompFim] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // Trocar filtro com a posicao antiga cairia num offset que ja nao existe.
  useEffect(() => { setPagina(1); }, [prefFiltro, compIni, compFim, statusFiltro]);

  // A CONSULTA MORA AQUI desde a 007, e nao no componente pai: os filtros e a
  // pagina fazem parte da chave dela agora. Deixa-la no pai obrigaria a subir
  // todo o estado de filtro junto, so para descer os dados de volta.
  //
  // A invalidacao das mutacoes do pai continua funcionando: elas invalidam
  // `["admin", "folhas"]`, que e prefixo desta chave.
  const data = useQuery({
    queryKey: ["admin", "folhas", prefFiltro, compIni, compFim, statusFiltro, pagina, porPagina],
    queryFn: () => atlas.admin.listFolhas({
      prefeitura_id: typeof prefFiltro === "number" ? prefFiltro : undefined,
      comp_ini: inputToComp(compIni) || undefined,
      comp_fim: inputToComp(compFim) || undefined,
      status: statusFiltro || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  const loading = data.isLoading;

  // FACETAS, CONTAGENS E TOTAIS VEM DA API desde a 007: os tres falam da BASE
  // ou do RECORTE, e a tela so tem a pagina. As opcoes dos filtros em especial
  // NAO podem encolher com o recorte — senao o operador nao teria como voltar
  // para o corte de onde acabou de sair.
  const facetas = data.data?.facetas ?? { prefeituras: [], competencias: [] };
  const prefs: [number, string][] = facetas.prefeituras.map((p) => [p.id, p.nome]);
  const compsExistentes = facetas.competencias;
  const contagemStatus = Object.fromEntries(
    (data.data?.contagemStatus ?? []).map((s) => [s.valor, s.contagem]),
  ) as Record<string, number>;
  const totalBase = data.data?.totalBase ?? 0;

  // Default competencia FIM = mais recente; INI = 12 meses antes. So pode ser
  // decidido depois que as facetas chegam — antes disso a tela nao sabe quais
  // competencias existem. O `!compFim` evita reescrever a escolha do operador
  // a cada refetch (a consulta repete a cada 5s).
  useEffect(() => {
    if (compsExistentes.length === 0 || compFim) return;
    const maisRecente = compsExistentes[compsExistentes.length - 1] ?? "";
    const doze = compsExistentes.length >= 12
      ? (compsExistentes[compsExistentes.length - 12] ?? "")
      : (compsExistentes[0] ?? "");
    setCompIni(compToInput(doze));
    setCompFim(compToInput(maisRecente));
  }, [compsExistentes, compFim]);

  // A pagina JA vem filtrada e ordenada por competencia desc.
  const daPagina = recorteDoServidor(data.data?.folhas ?? [], data.data?.total ?? 0, pagina, porPagina);

  // "Limpar filtros" volta ao default da tela (ultimas 12 competencias), nao a
  // ausencia de filtro — e o que ela sempre fez. O `setCompFim("")` deixa o
  // efeito acima recalcular a janela a partir das facetas, em vez de repetir a
  // regra das 12 competencias aqui.
  const reset = () => { setPrefFiltro(""); setCompIni(""); setCompFim(""); setStatusFiltro(""); setPagina(1); };

  return (
    <>
      {/* Status como chip: é a pergunta que o operador faz ao abrir a tela
          ("o que ainda não fechou?") e merece um clique, não um select. As
          contagens saem da base com prefeitura e competência já aplicadas,
          menos o próprio status — número que ignora os outros filtros mente
          sobre o que acontece ao clicar (FR-010). */}
      <FilterChips
        label="Status"
        value={statusFiltro}
        onChange={setStatusFiltro}
        options={[
          // "Todos" e a soma das contagens por status, ja calculadas com
          // prefeitura e competencia aplicadas e SEM o corte de status (FR-010).
          { value: "", label: "Todos", count: Object.values(contagemStatus).reduce((s, n) => s + n, 0) },
          { value: "aberta", label: "Abertas", cor: "var(--gold-500)", count: contagemStatus.aberta ?? 0 },
          { value: "fechada", label: "Fechadas", count: contagemStatus.fechada ?? 0 },
          { value: "consolidada", label: "Consolidadas", cor: "var(--emerald-500)", count: contagemStatus.consolidada ?? 0 },
        ]}
      />
      <div style={{
        display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap",
        padding: 12, background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: 10,
      }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Órgão/entidade
          <select
            value={prefFiltro}
            onChange={(e) => setPrefFiltro(e.target.value ? Number(e.target.value) : "")}
            style={{ minWidth: 220, padding: "8px 10px", borderRadius: 8, background: "var(--bg-elev-2)", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 13, textTransform: "none", fontWeight: 400, letterSpacing: 0 }}
          >
            <option value="">Todas</option>
            {prefs.map(([id, nome]) => (
              <option key={id} value={id}>{nome}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          De (competência)
          <input
            type="month"
            value={compIni}
            onChange={(e) => setCompIni(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, background: "var(--bg-elev-2)", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 13 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Até (competência)
          <input
            type="month"
            value={compFim}
            onChange={(e) => setCompFim(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, background: "var(--bg-elev-2)", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 13 }}
          />
        </label>
        <Button variant="ghost" onClick={reset} title="Volta pra últimas 12 competências / todas prefeituras">
          Limpar filtros
        </Button>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }}>
          {daPagina.total} de {totalBase} folha(s)
        </span>
      </div>
      <DataTable columns={columns} rows={daPagina.itens} rowKey={(f) => f.id} loading={loading} emptyState="Nenhuma folha registrada." />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="folhas"
      />
    </>
  );
}

/** `2026-09-15T17:00:00-03:00` → `15/09/2026 17:00`. */
function fmtInstante(iso: string | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const d = new Date(t);
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

/**
 * "1 de 2 partes" — o número que faltava.
 *
 * Folha que manda o retorno em mais de um arquivo (Morada Nova manda PREFEITURA
 * e PISO ENFERMAGEM) não podia ser lida na tela: o primeiro arquivo fechava a
 * competência e ninguém via que faltava metade.
 */
function CelulaRetorno({ estado }: { estado?: AdminEstadoDaFolha }) {
  if (!estado) return <span style={{ opacity: 0.5 }}>—</span>;
  const { partesEsperadas, partesRecebidas, partesFaltando, retornoCompleto, recebidosNaoProcessados } = estado;

  // CHEGOU x FOI APLICADO sao coisas diferentes, e a tela dizia "recebido" para as
  // duas. Com o processamento virando ato da averbadora, confundi-las esconderia
  // exatamente a fila de trabalho dela.
  const pendente = recebidosNaoProcessados > 0
    ? (
      <span
        style={{ fontSize: 11, color: "var(--gold-500)", fontWeight: 600 }}
        title="Chegou e está guardado. Nada foi aplicado até alguém processar."
      >
        {recebidosNaoProcessados} a processar
      </span>
    )
    : null;

  if (partesEsperadas.length === 0) {
    return (
      <div style={{ display: "grid", gap: 2 }}>
        {retornoCompleto
          ? <Pill variant="aceita">processado</Pill>
          : recebidosNaoProcessados > 0
            ? <Pill variant="pendente">recebido</Pill>
            : <span style={{ fontSize: 12, color: "var(--gold-500)" }}>aguardando</span>}
        {pendente}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <span style={{ fontSize: 12 }}>
        {partesRecebidas.length} de {partesEsperadas.length} partes
      </span>
      {partesFaltando.length > 0 ? (
        <span style={{ fontSize: 11, color: "var(--gold-500)" }} title={`Falta: ${partesFaltando.join(", ")}`}>
          falta {partesFaltando.join(", ")}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: "var(--emerald-500)" }}>completo</span>
      )}
      {pendente}
    </div>
  );
}
